import { useAbTesting } from '@packages/abtesting';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import { Button, CircularProgress, DialogPreset, Link, Notification } from '@packages/shared';
import { useSession } from '@packages/utilities';
import { Basket32 } from '@packages/themes/icons';
import { useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { mutate } from 'swr';
import { selectedQuantity, selectedServices } from '../../atoms';
import { AddToBasketLayer } from '../AddToBasketLayer';
import { useAddToBasket } from './useAddToBasket';
import { addToBasketMessages } from './messages';
import { useAddToBasketTracking } from '../Tracking/hooks/useAddToBasketTracking';
import { getSkuPart } from '../../helpers/getSkuPart';
import { addBranchToOrderUrlPath } from '../../helpers/addBranchToOrderUrlPath';

const AddToBasketFragment = graphql(/* GraphQL */ `
  fragment AddToBasketFragment on Product {
    akl
    sku
    marketcode
    availability {
      isOrderable
      state
    }
    price {
      currency
      value
      valueOld
      saving {
        value
      }
    }
    ...AddToBasketLayerFragment
    ...AddToBasketTrackingFragment
  }
`);

export type AddToBasketButtonProps = {
  /**
   * addToBasket fragment data
   */
  data: FragmentType<typeof AddToBasketFragment>;
};

export const AddToBasketButton = ({ data: productData }: AddToBasketButtonProps) => {
  const product = getFragmentData(AddToBasketFragment, productData);
  const {
    akl,
    availability: { state, isOrderable },
    sku,
    price,
  } = product;

  const [dialogOpened, setDialogOpened] = useState(false);
  const [openErrorNotification, setOpenErrorNotification] = useState(false);
  const [openWarningNotification, setOpenWarningNotification] = useState(false);
  // these atoms has to be outsourced into a wrapper when AddToBasketButton gets migrated into modules
  // in future this component will also be used from domain order
  const quantity = useAtomValue(selectedQuantity);
  const serviceLineitems = useAtomValue(selectedServices);

  const { formatMessage } = useIntl();
  const { jwtToken, loading, loggedIn } = useSession();
  const { setDefaultProductOutcome } = useAbTesting();

  const isAvailable = state !== 'UNSELECTED' ? isOrderable : true;

  const { trigger, cartData, isMutating } = useAddToBasket();

  const {
    trackLayerClosed,
    trackToBasketClick,
    trackToCheckAndOrder,
    trackAddToCart,
    trackCatalogGAddedProduct,
    trackGlycerinAddToCart,
    getTrackingParameter,
  } = useAddToBasketTracking({
    data: product,
  });

  const isLoading = loading || isMutating;

  useEffect(() => {
    if (dialogOpened && cartData) {
      if (!cartData.success) {
        setOpenErrorNotification(true);
        setDialogOpened(false);
      } else if (cartData.success) {
        trackToCheckAndOrder();
        setDefaultProductOutcome(
          [
            {
              count: quantity,
              masterId: akl,
              sku: getSkuPart(sku, 'ArticleNumberAndSize'),
              value: price.value / 100,
            },
          ],
          'ATB',
        );

        trackAddToCart();
        trackGlycerinAddToCart();
        trackCatalogGAddedProduct();
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartData, dialogOpened]);

  const onClickHandler = async () => {
    if (state === 'UNSELECTED') {
      setOpenWarningNotification(true);
    } else if (cartData && !cartData.success) {
      setOpenErrorNotification(true);
    } else {
      try {
        await trigger({
          sku,
          count: quantity,
          serviceLineitems,
          basketOption: [],
          isSpecialOrder: false,
          trackingParameter: getTrackingParameter(),
        });

        // revalidate basket abstract data after successful basket update
        await mutate(
          jwtToken ? [addBranchToOrderUrlPath('/api/checkout/v1/abstract'), jwtToken] : null,
        );
        setDialogOpened(true);
      } catch (err) {
        // TODO(error-handling): add service
        setOpenErrorNotification(true);
      }
    }
  };

  const trackToCheckAndOrderHandler = () => {
    trackToBasketClick();
  };

  const layerCloseHandler = () => {
    setDialogOpened(false);
    trackLayerClosed();
  };

  return (
    <>
      {openWarningNotification && (
        <Notification
          open={openWarningNotification}
          severity="warning"
          autoHideDuration={5000}
          onClose={() => setOpenWarningNotification(false)}
        >
          {formatMessage(addToBasketMessages.warningMessage)}
        </Notification>
      )}
      {openErrorNotification && (
        <Notification
          open={openErrorNotification}
          severity="error"
          autoHideDuration={5000}
          onClose={() => setOpenErrorNotification(false)}
        >
          {!cartData?.success
            ? cartData?.successMessages.elements[0].description
            : formatMessage(addToBasketMessages.errorMessage)}
        </Notification>
      )}
      {dialogOpened && cartData && cartData.success && (
        <DialogPreset
          title={formatMessage(addToBasketMessages.headline)}
          maxWidth="lg"
          stickyFooter
          buttons={
            <>
              {loggedIn && (
                <Button
                  LinkComponent={Link}
                  onClick={trackToCheckAndOrderHandler}
                  href="/kasse/zahlung"
                  color="primary"
                  size="large"
                  sx={{ maxWidth: ['100%', '100%', '180px'] }}
                  fullWidth
                >
                  {formatMessage(addToBasketMessages.toCheckout)}
                </Button>
              )}
              <Button
                LinkComponent={Link}
                onClick={trackToCheckAndOrderHandler}
                href="/warenkorb"
                color={loggedIn ? 'inherit' : 'primary'}
                size="large"
                sx={{ maxWidth: ['100%', '100%', '180px'] }}
                fullWidth
              >
                {formatMessage(addToBasketMessages.navigateToCart)}
              </Button>
              <Button
                variant="text"
                size="large"
                onClick={layerCloseHandler}
                sx={{ maxWidth: ['100%', '100%', '180px'] }}
                fullWidth
              >
                {formatMessage(addToBasketMessages.continueShopping)}
              </Button>
            </>
          }
          open={dialogOpened}
          onClose={layerCloseHandler}
        >
          <AddToBasketLayer
            lineItems={cartData.newPLIs.length > 0 ? cartData.newPLIs : undefined}
            data={product}
          />
        </DialogPreset>
      )}
      <Button
        endIcon={isLoading ? <CircularProgress color="secondary" size="s" /> : <Basket32 />}
        color="primary"
        size="large"
        onClick={onClickHandler}
        fullWidth
        sx={{ whiteSpace: 'nowrap' }}
        disabled={!isAvailable || isLoading}
      >
        {formatMessage(isAvailable ? addToBasketMessages.available : addToBasketMessages.soldOut)}
      </Button>
    </>
  );
};
